import { Component, OnInit, inject, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, EMPTY } from 'rxjs';
import { IBrandInfo } from '../../../interfaces/brand/brand-info';
import { ISessionDetails } from '../../../interfaces/session-details';
import { SharedService } from '../../../services/shared.service';
import { EnrollmentService } from '../../../services/enrollment/enrollment.service';
import { DataExchangeService } from '../../../services/session/data-exchange.service';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'app-nonmobile-page-enhanced',
    standalone: true,
    imports: [
        ReactiveFormsModule
    ],
    templateUrl: './nonmobile-page-enhanced.component.html',
    styleUrl: './nonmobile-page-enhanced.component.css'
})
export class NonmobilePageEnhancedComponent implements OnInit {

  @Input() data: any;

  private readonly router = inject(Router);
  private readonly sharedService = inject(SharedService);
  private readonly dataExchangeService = inject(DataExchangeService);
  private readonly enrollmentService  = inject(EnrollmentService);  
  private readonly logger = inject(NGXLogger);

  successMessage!: string;
  errorMessage!: string;
  isSuccessMessageShown: boolean = false;
  isErrorMessageShown: boolean = false;

  region!: string;
  session!: ISessionDetails;

  title!: string;
  isTitleShown: boolean = false;

  subtitle!: string;
  isSubtitleShown: boolean = false;

  emailLabel!: string;
  emailPrompt!: string;
  emailButton!: string;
  isEmailShown: boolean = false;

  smsLabel!: string;
  smsPrompt!: string;
  smsButton!: string;  
  isSMSShown: boolean = false;

  smsDisclaimer!: string;  
  isSMSDisclaimerShown: boolean = false;

  qrInvitationText!: string;
  isQrCodeUrlShown: boolean = false;

  qrCodeUrl!: string;

  public phoneForm!: FormGroup;
  public emailForm!: FormGroup;
  public emailAddress!: string;
  public phoneNumber!: string;

  constructor(private fb: FormBuilder) {
  }

  ngOnInit() {
    this.region = this.dataExchangeService.getRegion();
    this.session = this.dataExchangeService.getSession();
    if (this.region && this.session) {
      // Retrieve the QR code url
      this.getQRCode(this.region, this.session.walPageUuid || '', this.session.source);
    }
    this.phoneForm = this.fb.group({
      phoneNumber: [this.phoneNumber, [Validators.pattern("^(\\([0-9]{3}\\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$")]]
    });
    this.emailForm = this.fb.group({
      // emailAddress: [this.emailAddress, [Validators.email]]
      emailAddress: [this.emailAddress, [Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")]]
    });
    this.setCustomizations();
  }

  public setCustomizations() {
    let brandInfo: IBrandInfo = this.dataExchangeService.getBrandInfo();
    let isSmsEnabled : boolean = Boolean(this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_IS_SMS_ENABLED') === "true");
    this.title = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_TITLE');
    this.subtitle = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_SUBTITLE');
    this.emailLabel = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_EMAIL_INPUT_LABEL');
    this.emailPrompt = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_EMAIL_INPUT_PROMPT');
    this.emailButton = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_EMAIL_INPUT_BUTTON');
    this.smsLabel = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_SMS_INPUT_LABEL');
    this.smsPrompt = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_SMS_INPUT_PROMPT');
    this.smsButton = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_SMS_INPUT_BUTTON');
    this.smsDisclaimer = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_SMS_DISCLAIMER');
    this.qrInvitationText = this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_QR_INVITE_TEXT');
    this.isSubtitleShown = this.subtitle ? true : false;
    this.isTitleShown = this.title ? true : false;
    this.isEmailShown = (this.emailLabel && this.emailButton) ? true : false;
    this.isSMSShown = (isSmsEnabled && this.smsLabel && this.smsLabel.trim() !== "" && 
                        this.smsButton && this.smsButton.trim() !== "") ? true : false;
    this.isSMSDisclaimerShown = this.smsDisclaimer ? true : false;
  }


  public getQRCode(region: string, sessionId: string, source?: string) {
    // Make a web api call
    this.enrollmentService.getQRCode(region, sessionId, source)
    .subscribe({
      next: (response) => {
        if (response) {
          this.qrCodeUrl = response.qrUrl;
          this.isQrCodeUrlShown = this.qrInvitationText && this.qrCodeUrl ? true : false;        }
      },
      error: (error) => {
        // this.logger.error('NonmobilePageEnhancedComponent - getQRCode', error);
        this.dataExchangeService.setMessageText(error);
        this.router.navigate(['/err']);
      },
      complete: () => {
      }
    });
  }

  onSendSMS() {
    this.isSuccessMessageShown = false;
    this.isErrorMessageShown = false;
    this.enrollmentService.sendSMSInvitation(this.region, this.session.walPageUuid, this.phoneForm.value.phoneNumber)
    .subscribe({
      next: (response) => {
          // this.logger.debug('NonmobilePageEnhancedComponent - onSendSMS', response);
          this.successMessage = response.returnMessage; 
          this.isSuccessMessageShown = true;
      },
      error: (error) => {
        // this.logger.error('NonmobilePageEnhancedComponent - onSendSMS', error);
        this.dataExchangeService.setMessageText(error);
        this.router.navigate(['/err']);
      },
      complete: () => {
      }
    });
  }

  onSendEmail() {
    this.isSuccessMessageShown = false;
    this.isErrorMessageShown = false;
    this.enrollmentService.sendEmailInvitation(this.region, this.session.walPageUuid, this.emailForm.value.emailAddress)
    .subscribe({
      next: (response) => {
          // this.logger.debug('NonmobilePageEnhancedComponent - onSendEmail', response);
          this.successMessage = response.returnMessage; //'An email invitation has been sent out.';
          this.isSuccessMessageShown = true;
      },
      error: (error) => {
        // this.logger.error('NonmobilePageEnhancedComponent - onSendEmail', error);
        this.dataExchangeService.setMessageText(error);
        this.router.navigate(['/err']);
      },
      complete: () => {
      }
    });
  }

  onClose() {
    window.open('','_parent','');
    window.close();
  }  

  formChanged() {
  }    



}
